// API Endpoints - Same as mobile app
//
// Development falls back to a local API. A production bundle must say where its
// API is: scripts/checkBuildEnv.js (npm `prebuild`) stops the build when
// REACT_APP_API_URL is unset, and this guard catches a bundle produced some
// other way. Falling back to localhost in production builds and deploys
// cleanly, then sends every request to the user's own machine.
const DEV_API_URL = "http://localhost:3001";
const CONFIGURED_API_URL = process.env.REACT_APP_API_URL;
if (!CONFIGURED_API_URL && process.env.NODE_ENV === "production") {
  throw new Error(
    "REACT_APP_API_URL was not set at build time — refusing to fall back to a local API."
  );
}
const BASE_URL = CONFIGURED_API_URL || DEV_API_URL;
const API_ENDPOINTS = {
  loginUser: `${BASE_URL}/CRM/api/v1/user/loginUser`,
  logoutUser: `${BASE_URL}/CRM/api/v1/user/logoutUser`,
  logoutOtherUser: `${BASE_URL}/CRM/api/v1/user/logoutUser`,
  getLeadData: `${BASE_URL}/CRM/api/v1/lead/getLead`,
  followUps: `${BASE_URL}/CRM/api/v1/lead/followups`,
  leadDispositions: `${BASE_URL}/CRM/api/v1/lead/dispositions`,
  // Single-disposition endpoints (create/update/get)
  disposition: `${BASE_URL}/CRM/api/v1/lead/disposition`,
  // Backend route that adds disposals (alias for disposeLead)
  addLeadDisposition: `${BASE_URL}/CRM/api/v1/lead/addLeadDisposition`,
  // Get dispositions filtered by org
  getDispositionsByOrg: `${BASE_URL}/CRM/api/v1/lead/disposition/getDispositions`,
  users: `${BASE_URL}/CRM/api/v1/user/`,
  getUsers: `${BASE_URL}/CRM/api/v1/user/getUser`,
  createUser: `${BASE_URL}/CRM/api/v1/user/createUser`,
  updateUser: (orgId, userCode) => `${BASE_URL}/CRM/api/v1/user/updateUser/${orgId}/${userCode}`,
  resetPassword: `${BASE_URL}/CRM/api/v1/user/resetPassword`,
  changePassword: `${BASE_URL}/CRM/api/v1/user/changePassword`,
  calls: `${BASE_URL}/CRM/api/v1/calling`,
  initiateCall: `${BASE_URL}/CRM/api/v1/calling/initiate`,
  // WhatsApp hand-off to WABA Talk. POST (the session rides in x-api-key, which
  // a browser navigation cannot send); returns a redirectUrl to open.
  chatOpen: `${BASE_URL}/CRM/api/v1/nicechat/chat/open`,
  // Agent Assist ("Continue Customer"): GET = availability, POST = one-time launch link.
  agentAssist: (leadId) => `${BASE_URL}/CRM/api/v1/lead/${encodeURIComponent(leadId)}/agent-assist`,
  // Tenant-owned context for one lead, already role-filtered and masked by the server.
  leadContext: (leadId) => `${BASE_URL}/CRM/api/v1/lead/${encodeURIComponent(leadId)}/context`,
  // Live NeoFinQ picture for a lead (summary | applications | quotes | timeline), proxied by the CRM backend.
  leadNeofinq: (leadId, view) => `${BASE_URL}/CRM/api/v1/lead/${encodeURIComponent(leadId)}/neofinq/${encodeURIComponent(view)}`,
  // SMS via WABA Talk's existing send endpoint; the CRM half authorises the
  // lead and the template, then forwards.
  smsTemplates: `${BASE_URL}/CRM/api/v1/nicechat/sms/templates`,
  smsSend: `${BASE_URL}/CRM/api/v1/nicechat/sms/send`,
  updateCall: `${BASE_URL}/CRM/api/v1/updateCallDetails`,
  agentCallSummary: `${BASE_URL}/CRM/api/v1/agent/summary`,
  templateUsers: `${BASE_URL}/CRM/api/v1/fileupload/template/users`,
  templateLeads: `${BASE_URL}/CRM/api/v1/fileupload/template/leads`,
  uploadUsersCsv: `${BASE_URL}/CRM/api/v1/fileupload/upload/users`,
  uploadLeadsCsv: `${BASE_URL}/CRM/api/v1/fileupload/upload/leads`,
  uploadStatus: `${BASE_URL}/CRM/api/v1/fileupload/details`,
  uploadDetails: (uploadId) => `${BASE_URL}/CRM/api/v1/fileupload/admin/details/${uploadId}`,
  uploadPasswords: (uploadId) => `${BASE_URL}/CRM/api/v1/fileupload/admin/passwords/${uploadId}`,
  uploadHistory: `${BASE_URL}/CRM/api/v1/fileupload/history`,
  uploadDelete: (uploadId) => `${BASE_URL}/CRM/api/v1/fileupload/delete/${uploadId}`,
  rtmData: `${BASE_URL}/CRM/api/v1/rtm/data`,
  rtmStats: `${BASE_URL}/CRM/api/v1/rtm/stats`,
  rtmSSE: `${BASE_URL}/CRM/api/v1/rtm/stream`,
  rtmStream: `${BASE_URL}/CRM/api/v1/rtm/stream`,
  createLead: `${BASE_URL}/CRM/api/v1/lead/createLead`,
  updateLead: (leadId) => `${BASE_URL}/CRM/api/v1/lead/updateLead/${leadId}`,
  allocateLead: `${BASE_URL}/CRM/api/v1/lead/allocate`,
  markLeadImportant: `${BASE_URL}/CRM/api/v1/lead/markImportant`,
  unmarkLeadImportant: `${BASE_URL}/CRM/api/v1/lead/unmarkImportant`,
  getRejectLeads: `${BASE_URL}/CRM/api/v1/lead/reject/list`,
  disposeLeadBulk: `${BASE_URL}/CRM/api/v1/lead/dispose/bulk`,
  exportLeads: `${BASE_URL}/CRM/api/v1/lead/export`,
  exportUsers: `${BASE_URL}/CRM/api/v1/user/export`,
  exportAllocationSummary: `${BASE_URL}/CRM/api/v1/admin/export/allocation-summary`,
  exportCallSummary: `${BASE_URL}/CRM/api/v1/admin/export/call-summary`,
  agentSummary: `${BASE_URL}/CRM/api/v1/agent/summary`,
  teamPerformance: `${BASE_URL}/CRM/api/v1/agent/team-performance`,
  teamPerformanceExport: `${BASE_URL}/CRM/api/v1/agent/team-performance/export`,
  ipManagement: {
    getAllowedIPs: `${BASE_URL}/CRM/api/v1/admin/ip-management/allowed-ips`,
    addIP: `${BASE_URL}/CRM/api/v1/admin/ip-management/add-ip`,
    removeIP: (ipId) => `${BASE_URL}/CRM/api/v1/admin/ip-management/remove-ip/${ipId}`,
    toggle: `${BASE_URL}/CRM/api/v1/admin/ip-management/toggle`,
  },
  version: {
    check: `${BASE_URL}/CRM/api/v1/version/check`,
    info: `${BASE_URL}/CRM/api/v1/version/info`,
    update: `${BASE_URL}/CRM/api/v1/version/update`,
  },
  priorityConfig: `${BASE_URL}/CRM/api/v1/admin/priority-config`,
  webphoneConfig: `${BASE_URL}/CRM/api/v1/admin/webphone-config`,
  queueManagement: {
    list: `${BASE_URL}/CRM/api/v1/admin/queue-management/queues`,
    create: `${BASE_URL}/CRM/api/v1/admin/queue-management/queues`,
    update: (queueId) => `${BASE_URL}/CRM/api/v1/admin/queue-management/queues/${queueId}`,
    allocationConfig: (queueId) => `${BASE_URL}/CRM/api/v1/admin/queue-management/queues/${queueId}/allocation-config`,
    allocationByUser: (queueId) => `${BASE_URL}/CRM/api/v1/admin/queue-management/queues/${queueId}/allocation-by-user`,
  },
  // NicePhone Mini
  nicephone: {
    originate: `${BASE_URL}/CRM/api/v1/nicephone/originate`,
    callControl: `${BASE_URL}/CRM/api/v1/nicephone/call-control`,
    callMetrics: `${BASE_URL}/CRM/api/v1/nicephone/call-metrics`,
    webhook: `${BASE_URL}/CRM/api/v1/nicephone/webhook`,
    events: `${BASE_URL}/CRM/api/v1/nicephone/events`,
    activeCall: `${BASE_URL}/CRM/api/v1/nicephone/active-call`,
    // Calls stuck without a webhook: the agent ends their own; supervisors list / close any in the org.
    closeActiveCall: `${BASE_URL}/CRM/api/v1/nicephone/active-call/close`,
    staleCalls: (leadId) => `${BASE_URL}/CRM/api/v1/nicephone/stale-calls${leadId ? `?leadId=${encodeURIComponent(leadId)}` : ''}`,
    closeCall: (callId) => `${BASE_URL}/CRM/api/v1/nicephone/calls/${encodeURIComponent(callId)}/close`,
    // Browser SIP bootstrap. identity returns this advisor's own NicePhone user
    // id plus a short-lived token; credentials is the backend proxy the vendor
    // widget fetches SIP details from, so the provider key stays server-side.
    identity: `${BASE_URL}/CRM/api/v1/nicephone/identity`,
    availableUsers: `${BASE_URL}/CRM/api/v1/nicephone/available-users`,
    credentials: `${BASE_URL}/CRM/api/v1/nicephone/credentials`,
  },
  // UMS / LMS - Lead Reallocation
  ums: {
    reallocate: `${BASE_URL}/CRM/api/v1/admin/ums/reallocate`,
  },
  // Hierarchy Management
  hierarchy: {
    assignTlToManager: `${BASE_URL}/CRM/api/v1/admin/hierarchy/assign-tl-to-manager`,
    assignAdvisorToTl: `${BASE_URL}/CRM/api/v1/admin/hierarchy/assign-advisor-to-tl`,
    getManagerTeam: (managerId) => `${BASE_URL}/CRM/api/v1/admin/hierarchy/manager/${managerId}/team`,
    getTlAdvisors: (tlId) => `${BASE_URL}/CRM/api/v1/admin/hierarchy/tl/${tlId}/advisors`,
    unassign: (userId) => `${BASE_URL}/CRM/api/v1/admin/hierarchy/unassign/${userId}`,
    myTeam: `${BASE_URL}/CRM/api/v1/admin/hierarchy/my-team`,
  },
  // Permission Management
  permissions: {
    get: `${BASE_URL}/CRM/api/v1/admin/permissions`,
    update: (role) => `${BASE_URL}/CRM/api/v1/admin/permissions/${role}`,
  },
  // Scheduled Email Report config
  scheduledReport: `${BASE_URL}/CRM/api/v1/admin/scheduled-report`,
  // User suggest (auto-complete)
  suggestUsers: `${BASE_URL}/CRM/api/v1/user/suggest`,
  // Superadmin spoof
  spoofSearch: `${BASE_URL}/CRM/api/v1/user/spoof/search`,
  spoofStart: `${BASE_URL}/CRM/api/v1/user/spoof/start`,
  // Advisor endpoints
  getNextLead: `${BASE_URL}/CRM/api/advisor/leads/next`,
  startCall: `${BASE_URL}/CRM/api/advisor/call/start`,
  saveDisposition: (leadId) => `${BASE_URL}/CRM/api/advisor/leads/${leadId}/disposition`,
  // Notes endpoints
  createNote: `${BASE_URL}/CRM/api/v1/notes`,
  getNotes: (leadId) => `${BASE_URL}/CRM/api/v1/notes?leadId=${leadId}`,
  // Agent notes widget
  agentNotes: `${BASE_URL}/CRM/api/v1/notes/agent`,
  // Call history endpoints
  getCallHistory: (leadId) => `${BASE_URL}/CRM/api/v1/calling/history?leadId=${leadId}`,
  searchCallHistory: `${BASE_URL}/CRM/api/v1/calling/search`,
  updateCallRating: `${BASE_URL}/CRM/api/v1/calling/updateCallDetails`,
  /** Save call lifecycle events from frontend tracking */
  saveCallEvents: `${BASE_URL}/CRM/api/v1/calling/events`,

  /**
   * Superadmin: tenant (organisation) management. SuperAdminOrgs.jsx referenced
   * these before they existed, so the page threw on load.
   */
  superadmin: {
    organisations: `${BASE_URL}/CRM/api/v1/superadmin/organisations`,
    organisation: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}`,
    features: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/features`,
    orgUsers: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/users`,
    orgUser: (orgId, userCode) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/users/${encodeURIComponent(userCode)}`,
    orgUserResetPassword: (orgId, userCode) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/users/${encodeURIComponent(userCode)}/reset-password`,
    // Customer-thread rules (one lead per customer × product) of one organisation.
    orgThreadConfig: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/thread-config`,
    orgPermissions: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/permissions`,
    orgRolePermissions: (orgId, role) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/permissions/${role}`,
    // Tenant API keys for the lead push API (superadmin only). The secret is only ever in the create response.
    integrationCredentials: `${BASE_URL}/CRM/api/v1/superadmin/integration-credentials`,
    integrationCredentialRevoke: (publicKey) => `${BASE_URL}/CRM/api/v1/superadmin/integration-credentials/${encodeURIComponent(publicKey)}/revoke`,
    orgQueues: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/queues`,
    // WABA Talk connection for one organisation (superadmin only).
    orgNicechat: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/integrations/nicechat`,
    // Server health (this API process) and its email alerts.
    // Partner API call logs: every inbound partner call and every call we make to a partner.
    apiLogs: `${BASE_URL}/CRM/api/v1/superadmin/api-logs`,
    apiLogFilters: `${BASE_URL}/CRM/api/v1/superadmin/api-logs/filters`,
    apiLog: (id) => `${BASE_URL}/CRM/api/v1/superadmin/api-logs/${encodeURIComponent(id)}`,
    systemMetrics: `${BASE_URL}/CRM/api/v1/superadmin/system/metrics`,
    systemAlerts: `${BASE_URL}/CRM/api/v1/superadmin/system/alerts`,
    systemAlertsTest: `${BASE_URL}/CRM/api/v1/superadmin/system/alerts/test`,
    orgNicephone: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/integrations/nicephone`,
    // Ask the mapped WABA Talk tenant what lines and templates it has.
    orgNicechatDiscover: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/integrations/nicechat/discover`,
    // WABA Talk tenant connection. The browser only ever sends a tenant id; the
    // platform credential and the tenant API key stay on the Relabix server.
    wabaTalkTenants: `${BASE_URL}/CRM/api/v1/superadmin/integrations/wabatalk/tenants`,
    wabaTalk: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/integrations/wabatalk`,
    wabaTalkSync: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/integrations/wabatalk/sync`,
    wabaTalkHealth: (orgId) => `${BASE_URL}/CRM/api/v1/superadmin/organisations/${orgId}/integrations/wabatalk/health`,
  },
  /** WebSocket endpoint - configurable via REACT_APP_WS_URL */
  webSocket: (() => {
    // Check for explicit WebSocket URL in environment
    if (process.env.REACT_APP_WS_URL) {
      return process.env.REACT_APP_WS_URL;
    }
    
    // Determine protocol (wss for production, ws for localhost)
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const protocol = isLocalhost ? 'ws' : 'wss';
    
    // Extract hostname from BASE_URL (remove http:// or https://)
    const urlMatch = BASE_URL.match(/https?:\/\/([^\/]+)/);
    let hostname = urlMatch ? urlMatch[1] : 'api-staging-crm.nicedialtech.com';
    
    // Remove port 3000 if present (should not be used in production)
    // Also remove any port numbers in production (use default ports)
    if (!isLocalhost) {
      hostname = hostname.replace(/:3000$/, '').replace(/:(\d+)$/, '');
    }
    
    // Build WebSocket URL (use backend domain, not frontend)
    return `${protocol}://${hostname}/ws`;
  })(),
  baseURL: BASE_URL,
};

export { API_ENDPOINTS };
export default API_ENDPOINTS;
